import type { SortParams } from '@core/http-types';
import type {
  CampaignAnalyticsConfig,
  GfeaOffer,
  GfeaPosting,
  HotelProfessionalsPosting,
  WerkenInEenHotelPosting,
} from '@core/integrations-legacy';
import type { LocationBrief } from '@core/locations-types';
import type { OfferStatus, TOfferKind } from '@core/offers-types';
import type { SelectOption } from '@core/user-interface-legacy';

// Currently a settings item can be related to:
//   - job board
//   - campaign analytics config
//   - both (job board that also has campaign analytics)
export type JobBoardSettingsItem = {
  codename: string;
  name: string;
  website: string;
  logoUrl: string | null;
  jobBoard?: JobBoard;
  campaignAnalyticsConfig?: CampaignAnalyticsConfig;
};

export type JobBoard = {
  id: number;
  recommended: boolean;
  customIntegration: boolean;
  codename: string;
  name: string;
  description: string;
  website: string;
  autoPosting: boolean;
  autoPostingDisabled: boolean;
  logoUrl: string | null;
  credentialsStructure: CredentialsStructure | null;
  phaseout: JobBoardPhaseout | null;
  credentials?: JobBoardCredential[];
  posting?: JobBoardPosting;
  validation?: JobBoardPostingValidation | null;
};

export type IntegrationJobBoard = Omit<JobBoard, 'posting'> & {
  duplicateOffer?: GfeaOffer;
  posting?: WerkenInEenHotelPosting | GfeaPosting | HotelProfessionalsPosting;
  location?: LocationBrief;
};

export type JobBoardsCounters = {
  all: number;
  published: number;
};

export type CredentialsStructure = CredentialFieldStructure[];

export enum CredentialFieldType {
  Email = 'email',
  Url = 'url',
  Text = 'text',
  Select = 'select',
}

export enum CredentialKind {
  Personal = 'personal',
  Company = 'company',
}

export type CredentialFieldStructure = {
  type: CredentialFieldType;
  key: JobBoardCredentialKey;
  required: boolean;
  secret: boolean;
  editable: boolean;
  format?: string;
  options?: SelectOption[];
};

export type JobBoardPhaseout = {
  helpArticleUrl: string | null;
};

export type JobBoardCredential = {
  id: number;
  name: string;
  kind?: CredentialKind | null;
  jobBoardId: number;
  editable: boolean;
  data: { [key: string]: string | number | null };
};

export type JobBoardPayloadParams = SortParams & { query?: string | null };

export type JobBoardCredentialCreateParams = {
  name: string;
  data: { [key: string]: string | number | null };
};

export type JobBoardCredentialUpdateParams = {
  name: string;
  data: { [key: string]: string | number | null };
};

export type JobBoardOfferConfig = {
  autoRepublish: boolean;
  companyId: number;
  offerId: number;
};

export type JobBoardPosting = {
  id: number;
  credentialId: number | null;
  status: JobBoardPostingStatus;
  publishedAt: string | null;
  createdAt: string;
  sync: JobBoardPostingSync | null;
  offer?: JobBoardPostingOffer;
  externalUrl: string | null;
  republishable: boolean;
  republishCount: number;
};

export type JobBoardPostingValidation = {
  message: string;
  messageShort: string;
  required: boolean;
  valid: boolean;
  validLocations?: number[];
};

export type JobBoardPostingSync = {
  error: string | null;
};

export enum JobBoardPostingStatus {
  Published = 'published',
  Rejected = 'rejected',
  Closed = 'closed',
  Expired = 'expired',
}

export type JobBoardPostingOffer = {
  id: number;
  slug: string;
  title: string;
  countryCode: string;
  city: string;
  adminappUrl: string;
  kind: TOfferKind;
  status: OfferStatus;
};

export type JobBoardPublishOfferParams = {
  offerId: number;
  credentialId?: number;
  posterEmail?: string;
};

export type JobBoardCompanyLogo = {
  id: number;
  url: string;
};

export enum JobBoardCredentialKey {
  FigaroClassifiedsCustomerId = 'figaro_classifieds_customer_id',
  IndeedWebsite = 'indeed_website',
  IndeedEmail = 'indeed_email',
  LinkedinCompanyId = 'linkedin_company_id',
  LinkedinPosterEmail = 'linkedin_poster_email',
  JobCloudProduct = 'jobcloud_product',
  JobCloudCompanyId = 'jobcloud_company_id',
  WorkInStartupsCompanyId = 'work_in_startups_company_id',
  DataRockstarsCompanyId = 'datarockstars_company_id',
}
